import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/switch/switch.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/avatar/avatar.js";

import { html } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { keyed } from "lit/directives/keyed.js";
import { BaseElement } from "../BaseElement";
import { watch } from "../internal/watch";
import { styles } from "./UserSettings.style";
import { IMedia } from "../../models/Models";
import { MAX_AVATAR_SIZE } from "../../Media";
import { litIf } from "../LitUtils";
import { IUserInfo } from "../../backend/cloud/UserTable";
import { ifDefined } from "lit/directives/if-defined.js";

import { DEFAULT_NAVIGATION_SENSITIVITY } from "../../Constants";
import { DisplayUnits } from "../../../models";

import { FormsT } from "../form/FormTypes";

import "../ImageMediaImport/ImageMediaImport";
import { showConfirmDialog } from "../ConfirmDialog";

export const UserFields: FormsT[] = [
    {
        type: "textInput",
        title: "Name that other users will see",
        property: "displayName",
        placeholder: "",
    },
    {
        type: "textArea",
        title: "Bio",
        property: "bio",
    },
    {
        type: "enumSelect",
        title: "Units",
        property: "displayUnits",
        enumType: DisplayUnits,
    },
];

export const UserSocialFields: FormsT[] = [
    {
        type: "textInput",
        title: "Facebook",
        property: "facebook",
        placeholder: "www.facebook.com/[this part]/",
        helpMsg: "Facebook profile link",
    },
    {
        type: "textInput",
        title: "Instagram",
        property: "instagram",
        placeholder: "www.instagram.com/[this part]/",
        helpMsg: "Instagram profile link",
    },
    {
        type: "textInput",
        title: "Blue Sky",
        property: "blueSky",
        placeholder: "bsky.app/profile/[this part]/",
        helpMsg: "Blue Sky profile link",
    },
    {
        type: "textInput",
        title: "Website",
        property: "website",
        placeholder: "www.mywebsite.com",
        helpMsg: "Personal website link",
    },
    {
        type: "textInput",
        title: "x",
        property: "x",
        placeholder: "www.x.com/[this part]/",
        helpMsg: "X profile link",
    },
];

@customElement("ds-user-settings-control")
export class UserSettingsControl extends BaseElement {
    static override styles = [styles];

    @property({ type: Boolean }) showAll = false;
    @state() protected _userInfo?: IUserInfo;
    @state() protected _currentNavigationSensitivity = 10;
    @state() protected _showCompass = this._kernel.showCompass.get();
    @state() protected _enableTileCache = this._kernel.enableTileCache.get();
    @state() protected _enableDebugInfo = this._kernel.enableDebugInfo.get();
    @state() protected _userAvatar?: IMedia;
    @state() protected _userAvatarUrl?: string;
    @state() protected _logLevel = this._kernel.loggingLevel.get();
    @state() protected _uploadingMedia = false;
    @state() protected _uploadProgress = 0;
    @state() protected _avatarError?: string;
    @state() protected _userId?: string;
    @state() protected _zoomOnFilterChange = this._kernel.zoomOnFilterChange.get();

    // experiments
    @state() protected _experimentFeatureBar = this._kernel.featureHeaderFilterBar.get();

    public constructor() {
        super();
    }

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._userId = this._kernel.user?.id;
        const settings = await this._kernel.userSettings.read(this._userId);
        if (settings) {
            // User settings
            this._currentNavigationSensitivity =
                settings.navigationAccuracy ?? DEFAULT_NAVIGATION_SENSITIVITY;
            this._userAvatar = settings.avatar ?? undefined;
            this._userInfo = settings;
        }
    }

    private async _save() {
        if (this._userInfo) {
            await this._kernel.userSettings.update(this._userInfo);
            this._kernel.saveUserSettings(this._userInfo);
        }
    }

    @watch("_currentNavigationSensitivity")
    public _navigationSensitivityChanged(oldValue: any, newValue: any) {
        if (this._userInfo && oldValue !== newValue) {
            this._userInfo.navigationAccuracy = newValue;
            this._save();
        }
    }

    @watch("_currentDisplayName")
    public _displayNameChanged(oldValue: any, newValue: any) {
        if (this._userInfo && oldValue !== newValue) {
            this._userInfo.displayName = newValue;
            this._save();
        }
    }

    @watch("_showCompass")
    public _showCompassChanged(oldValue: any, newValue: any) {
        if (oldValue !== newValue) {
            this._kernel.showCompass.set(newValue as boolean);
        }
    }

    @watch("_experimentFeatureBar")
    public _experimentalFeatureBarChanged(oldValue: any, newValue: any) {
        if (oldValue !== newValue) {
            this._kernel.featureHeaderFilterBar.set(newValue as boolean);
        }
    }

    @watch("_enableTileCache")
    public _enableTileCacheChanged(oldValue: any, newValue: any) {
        if (oldValue !== newValue) {
            this._kernel.enableTileCache.set(newValue as boolean);
        }
    }
    @watch("_enableDebugInfo")
    public _enableDebugInfoChanged(oldValue: any, newValue: any) {
        if (oldValue !== newValue) {
            this._kernel.enableDebugInfo.set(newValue as boolean);
        }
    }

    @watch("_zoomOnFilterChange")
    public _zoomOnFilterChangeChanged(oldValue: any, newValue: any) {
        if (oldValue !== newValue) {
            this._kernel.zoomOnFilterChange.set(newValue as boolean);
        }
    }

    @watch("_userAvatar")
    public async _userAvatarChanged(oldValue: any, newValue: any) {
        if (this._userInfo && oldValue !== newValue) {
            this._userAvatarUrl = await this._getUserAvatarUrl(newValue);
            this._userInfo.avatar = newValue;
            this._save();
        }
    }

    @watch("_logLevel")
    public _logLevelChanged(oldValue: any, newValue: any) {
        if (oldValue !== newValue) {
            this._kernel.loggingLevel.set(newValue as string);
        }
    }

    @watch("_units")
    public _unitsChanged(oldValue: any, newValue: any) {
        if (this._userInfo && oldValue !== newValue) {
            this._userInfo.displayUnits = newValue;
            this._save();
        }
    }

    private async _getUserAvatarUrl(avatar: IMedia) {
        this._userAvatarUrl = undefined;
        const img = await this._kernel.mediaLibrary.get(avatar.file.key);
        if (img) {
            this._userAvatarUrl = URL.createObjectURL(img);
        }
        return this._userAvatarUrl;
    }

    private _showAvatarError(msg: string) {
        this._avatarError = msg;
    }

    override render() {
        return html`
            <div class="settings-container flex-column">
                ${litIf(
                    this.showAll,
                    () =>
                        html` <div class="flex-row top-row">
                                <div class="user-avatar flex-row">
                                    <sl-avatar image=${ifDefined(this._userAvatarUrl)}></sl-avatar>
                                    <ds-image-media-import
                                        buttonText="Change avatar"
                                        iconName="camera"
                                        .maxSizeBytes=${MAX_AVATAR_SIZE}
                                        @ds-accept=${async (e: CustomEvent) => {
                                            if (this._userInfo) {
                                                this._userInfo.avatar = e.detail.media;
                                                await this._kernel.userSettings.update(
                                                    this._userInfo
                                                );
                                                this._userAvatar = e.detail.media;
                                                this._avatarError = undefined;
                                            }
                                        }}
                                        @ds-error=${(e: CustomEvent) => {
                                            this._showAvatarError(e.detail.error);
                                        }}
                                    ></ds-image-media-import>
                                </div>
                                <sl-button
                                    variant="warning"
                                    @click=${() =>
                                        showConfirmDialog(
                                            {
                                                open: true,
                                                prompt: "Sign out?",
                                                message:
                                                    "This will sign you out from the application. Press 'Yes' to sign out or 'Cancel' to abort.",
                                            },
                                            () => {
                                                this._kernel.signOut();
                                            }
                                        )}
                                    >Sign out</sl-button
                                >
                            </div>
                            ${litIf(
                                !!this._avatarError,
                                () => html`<span class="avatar-error">${this._avatarError}</span>`
                            )}
                            ${litIf(
                                this._userInfo,
                                () =>
                                    html`${keyed(
                                        this._userInfo,
                                        html`
                                            <ds-form
                                                .fields=${UserFields}
                                                .object=${this._userInfo}
                                                @ds-form-changed=${() => {
                                                    this._save();
                                                }}
                                            ></ds-form>
                                            <ds-form
                                                .fields=${UserSocialFields}
                                                .object=${this._userInfo?.socials}
                                                @ds-form-changed=${() => {
                                                    this._save();
                                                }}
                                            ></ds-form>
                                        `
                                    )}`
                            )}`
                )}

                <sl-switch
                    ?checked=${this._showCompass}
                    @sl-change=${(e: any) => {
                        this._showCompass = e.target.checked;
                    }}
                    >Show compass</sl-switch
                >
                <sl-switch
                    ?checked=${this._enableTileCache}
                    @sl-change=${(e: any) => (this._enableTileCache = e.target.checked)}
                >
                    Enable tile cache (requires reload)
                </sl-switch>
                <sl-switch
                    ?checked=${this._zoomOnFilterChange}
                    @sl-change=${(e: any) => {
                        this._zoomOnFilterChange = e.target.checked;
                    }}
                    >Zoom on filter change</sl-switch
                >

                <sl-switch
                    ?checked=${this._enableDebugInfo}
                    @sl-change=${(e: any) => (this._enableDebugInfo = e.target.checked)}
                >
                    Enable debug info
                </sl-switch>

                <div class="nav-sensitivity">
                    <span>Navigation sensitivity</span>
                    <sl-input
                        readonly
                        .value=${this._currentNavigationSensitivity.toString()}
                    ></sl-input>
                    <sl-icon-button
                        name="dash-lg"
                        @click=${() => {
                            this._currentNavigationSensitivity = Math.max(
                                this._currentNavigationSensitivity - 5,
                                5
                            );
                        }}
                    ></sl-icon-button>
                    <sl-icon-button
                        name="plus-lg"
                        @click=${() => {
                            this._currentNavigationSensitivity = Math.min(
                                this._currentNavigationSensitivity + 5,
                                45
                            );
                        }}
                    ></sl-icon-button>
                </div>

                <div class="flex-row" style="justify-content: space-between;align-items: center;">
                    <span>Logging level</span>
                    <sl-select
                        .value=${this._logLevel}
                        @sl-change=${(e: any) => {
                            this._logLevel = e.target.value;
                        }}
                    >
                        <sl-option value="trace">Trace</sl-option>
                        <sl-option value="debug">Debug</sl-option>
                        <sl-option value="info">Info</sl-option>
                        <sl-option value="warn">Warning</sl-option>
                        <sl-option value="error">Error</sl-option>
                    </sl-select>
                </div>
                <div slot="footer" class="footer-buttons">
                    ${litIf(
                        this.showAll,
                        () =>
                            html` <sl-button
                                variant="danger"
                                @click=${() => this._raiseEvent("ds-delete-user")}
                                >Delete account</sl-button
                            >`
                    )}
                </div>

                ${litIf(
                    this.showAll,
                    () =>
                        html` <div class="flex-row">
                            <span>User id: </span>${this._userId}<ds-copy-to-clipboard
                                .text=${this._userId}
                            ></ds-copy-to-clipboard>
                        </div>`
                )}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-user-settings-control": UserSettingsControl;
    }
}
